import { defineStore } from 'pinia'
import axios from '@/services/axios'


export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : null,
    token: localStorage.getItem('token'),
    refreshToken: localStorage.getItem('refreshToken'),
    isAuthenticated: !!localStorage.getItem('token'), // Set based on token existence
    loading: false,
    error: null,
    clientType: 'admin', // Default to admin, can be 'dtc', 'b2b', or 'admin'
    initialized: false // Track if auth has been initialized
  }),

  getters: {
    isLoggedIn: (state) => !!state.token && state.isAuthenticated,
    currentUser: (state) => state.user,
    userPermissions: (state) => state.user?.permissions || [],
    userRole: (state) => state.user?.role || null,
    organizationId: (state) => state.user?.organizationId || state.user?.clientId || null, // Backward compatibility
    clientId: (state) => state.user?.organizationId || state.user?.clientId || null // Deprecated, use organizationId
  },

  actions: {
    async login(credentials) {
      console.log('🔐 Auth Store: Starting login process...');
      console.log('   - Client Type:', this.clientType);
      console.log('   - Email:', credentials.email);
      console.log('   - Endpoint:', `/v1/${this.clientType}/auth/login`);
      
      this.loading = true
      this.error = null
      
      try {
        const loginUrl = `/v1/${this.clientType}/auth/login`;
        console.log('🌐 Auth Store: Making login request to:', loginUrl);
        console.log('   - Client type:', this.clientType);
        console.log('   - Axios base URL:', axios.defaults.baseURL);
        console.log('   - Full URL will be:', axios.defaults.baseURL + loginUrl);
        
        const response = await axios.post(loginUrl, credentials)
        console.log('✅ Auth Store: Login response received');
        console.log('   - Status:', response.status);
        console.log('   - Data keys:', Object.keys(response.data));
        console.log('   - Full response:', response.data);
        
        const { token, refreshToken, user, requiresMfa } = response.data.data
        
        if (requiresMfa) {
          // Store temp token for MFA
          this.token = response.data.data.tempToken
          return { requiresMfa: true, tempToken: response.data.data.tempToken }
        }
        
        console.log('🔑 Auth Store: Setting authentication...');
        this.setAuth(token, refreshToken, user)
        console.log('✅ Auth Store: Login completed successfully');
        return { success: true }
      } catch (error) {
        console.error('❌ Auth Store: Login failed');
        console.error('   - Error:', error.message);
        console.error('   - Response:', error.response?.data);
        console.error('   - Status:', error.response?.status);
        // Extract error message properly
        const errorData = error.response?.data?.error
        if (typeof errorData === 'string') {
          this.error = errorData
        } else if (errorData?.message) {
          this.error = errorData.message
        } else {
          this.error = 'Login failed'
        }
        throw error
      } finally {
        this.loading = false
      }
    },

    async verifyMfa(mfaToken, tempToken) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post(`/v1/${this.clientType}/auth/verify-mfa`, {
          mfaToken,
          tempToken
        })
        
        const { token, refreshToken, user } = response.data.data
        this.setAuth(token, refreshToken, user)
        return { success: true }
      } catch (error) {
        // Extract error message properly
        const errorData = error.response?.data?.error
        if (typeof errorData === 'string') {
          this.error = errorData
        } else if (errorData?.message) {
          this.error = errorData.message
        } else {
          this.error = 'MFA verification failed'
        }
        throw error
      } finally {
        this.loading = false
      }
    },

    async register(userData) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post(`/v1/${this.clientType}/auth/register`, userData)
        const { token, refreshToken, user } = response.data.data
        
        this.setAuth(token, refreshToken, user)
        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.error || 'Registration failed'
        throw error
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        if (this.token) {
          await axios.post(`/v1/${this.clientType}/auth/logout`, {}, {
            headers: { Authorization: `Bearer ${this.token}` }
          })
        }
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        this.clearAuth()
      }
    },

    async refreshAccessToken() {
      try {
        const response = await axios.post(`/v1/${this.clientType}/auth/refresh`, {
          refreshToken: this.refreshToken
        })
        
        const { token, refreshToken, user } = response.data.data
        this.setAuth(token, refreshToken, user)
        return token
      } catch (error) {
        this.clearAuth()
        throw error
      }
    },

    async fetchCurrentUser() {
      if (!this.token) {
        console.log('⚠️ Auth Store: No token available for fetchCurrentUser');
        return null;
      }
      
      try {
        console.log('👤 Auth Store: Fetching current user...');
        console.log('   - Client type:', this.clientType);
        console.log('   - Endpoint:', `/v1/${this.clientType}/auth/me`);
        console.log('   - Token:', this.token ? `${this.token.substring(0, 20)}...` : 'null');
        
        const response = await axios.get(`/v1/${this.clientType}/auth/me`, {
          headers: { Authorization: `Bearer ${this.token}` }
        })
        
        console.log('✅ Auth Store: fetchCurrentUser response received');
        console.log('   - Status:', response.status);
        console.log('   - Data keys:', Object.keys(response.data));
        console.log('   - User data:', response.data.data);
        
        this.user = response.data.data.user
        this.isAuthenticated = true
        localStorage.setItem('user', JSON.stringify(response.data.data.user))
        return this.user
      } catch (error) {
        console.error('❌ Auth Store: fetchCurrentUser failed');
        console.error('   - Error:', error.message);
        console.error('   - Status:', error.response?.status);
        console.error('   - Response data:', error.response?.data);
        
        if (error.response?.status === 401) {
          console.log('   - 401 error, clearing auth');
          this.clearAuth()
          // Don't throw the error if it's a 401 - just silently clear auth
          return null;
        }
        throw error
      }
    },

    setAuth(token, refreshToken, user) {
      console.log('🔑 Auth Store: Setting authentication...');
      console.log('   - Token length:', token?.length);
      console.log('   - Refresh token length:', refreshToken?.length);
      console.log('   - User ID:', user?.id);
      console.log('   - User role:', user?.role);
      console.log('   - Client type:', this.clientType);
      
      this.token = token
      this.refreshToken = refreshToken
      this.user = user
      this.isAuthenticated = true
      
      localStorage.setItem('token', token)
      localStorage.setItem('refreshToken', refreshToken)
      localStorage.setItem('clientType', this.clientType)
      localStorage.setItem('user', JSON.stringify(user))
      
      // Don't set default axios header here - it's handled by interceptors
      // axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      
      console.log('✅ Auth Store: Authentication set successfully');
    },

    clearAuth() {
      console.log('🧹 Auth Store: Clearing authentication...');
      console.log('   - Had token:', !!this.token);
      console.log('   - Had user:', !!this.user);
      console.log('   - Was authenticated:', this.isAuthenticated);
      
      this.token = null
      this.refreshToken = null
      this.user = null
      this.isAuthenticated = false
      this.initialized = false
      
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('clientType')
      localStorage.removeItem('user')
      
      // Don't delete axios header here - it's handled by interceptors
      // delete axios.defaults.headers.common['Authorization']
      
      // Reset to admin default
      this.clientType = 'admin';
      localStorage.setItem('clientType', 'admin');
      
      console.log('✅ Auth Store: Authentication cleared');
    },

    setClientType(type) {
      if (['dtc', 'b2b', 'admin'].includes(type)) {
        this.clientType = type
        localStorage.setItem('clientType', type)
      }
    },

    async initializeAuth() {
      // Skip if already initialized
      if (this.initialized) {
        console.log('⚠️ Auth Store: Already initialized, skipping');
        return;
      }
      
      // Mark as initializing to prevent concurrent calls
      this.initialized = true;
      
      // Initializing authentication
      
      const savedClientType = localStorage.getItem('clientType')
      
      if (savedClientType) {
        this.clientType = savedClientType
      } else {
        // Ensure we always have admin as default for this frontend
        this.clientType = 'admin';
        localStorage.setItem('clientType', 'admin');
      }
      
      // Token validation check
      
      if (this.token) {
        try {
          // Fetching current user
          await this.fetchCurrentUser()
          // User fetched successfully
        } catch (error) {
          console.error('❌ Auth Store: Failed to fetch user:', error)
          console.error('   - Error status:', error.response?.status);
          console.error('   - Error data:', error.response?.data);
          // Only clear auth if it's not a 401 (401 is already handled in fetchCurrentUser)
          if (error.response?.status !== 401) {
            this.clearAuth()
          }
        }
      } else {
        // No token found, skipping user fetch
      }
      
      // Auth initialization complete
    }
  }
})